import { LoanStatus, LoanStatusExternal, PaymentMethodType } from '@nemuru-eng/js-api-client';

import { ProgressPercentage } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/Progress/_shared/ProgressPercentage';
import {
  MilestoneProgressStages,
  NonActiveProgressStages,
  ProgressStep,
  ProgressStepStatus,
  Status,
  StatusView,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/Progress/_shared/types';

export abstract class AbstractProgress {
  protected _paymentMethodId: string;
  protected _paymentMethodType: PaymentMethodType;
  protected _progressStages: Partial<MilestoneProgressStages>;
  protected _statuses: StatusView[] | undefined = undefined;
  protected _currentStep: ProgressStep | undefined = undefined;
  protected _lastStatus: StatusView | undefined = undefined;
  protected _lastStatusInCurrentStep: StatusView | undefined = undefined;
  protected _progressPercentage: number | undefined = undefined;
  protected _isApplicationActive: boolean = false;
  protected _isApplicationCompleted: boolean = false;

  constructor(
    paymentMethodId: string,
    paymentMethodType: PaymentMethodType,
    progressStages: Partial<MilestoneProgressStages>,
  ) {
    this._paymentMethodId = paymentMethodId;
    this._paymentMethodType = paymentMethodType;
    this._progressStages = progressStages;
  }

  public async calculateProgress(): Promise<this> {
    this._statuses = await this.fetchStatuses();
    this._currentStep = this.findCurrentStep(); // First step of current stage
    this._lastStatusInCurrentStep = this.findLastStatusInCurrentStep();
    this._lastStatus = this.findLastStatus(); // Lattermost status in_statuses array with latest updatedAt date
    this._isApplicationActive = this.checkIfApplicationIsActive();
    this._isApplicationCompleted = this.checkIfApplicationIsCompleted();
    this._progressPercentage = this.getProgressPercentage();
    return this;
  }

  protected abstract fetchStatuses(): Promise<StatusView[]>;
  protected abstract getNonActiveStages(): NonActiveProgressStages;
  protected abstract isNonActiveStatus(status: Status): boolean;
  protected abstract isCompletedStatus(status: Status): boolean;
  protected abstract getStatusName(status: Status): string;

  private findCurrentStep(): ProgressStep | undefined {
    if (!this._statuses) {
      console.error('No statuses found in AbstractProgress.findCurrentStage');
      return;
    }

    // 1- inactive step
    const lastNonActiveStep = this.findLastNonActiveStep();
    if (lastNonActiveStep) {
      return lastNonActiveStep;
    }

    // 2- last valid step from current CheckpointGroup
    return this.findLastValidStep();
  }

  // Creates flat array of all progress steps of all stages
  private getProgressStepsFromStages(): ProgressStep[] {
    return Object.values(this._progressStages)
      .filter((stage) => !!stage)
      .flatMap((stage) => stage.flatMap((step) => step));
  }

  // Find the fist step of stage with currentCheckpointGroup
  private findLastValidStep = (): ProgressStep | undefined => {
    if (!this._statuses) {
      return undefined;
    }

    const currentCheckpointGroup = this.getCurrentCheckpointGroup();
    const progressSteps = this.getProgressStepsFromStages();

    let lastStep: ProgressStep | undefined = undefined;
    let lastValidStep: ProgressStep | undefined = undefined;

    // Find the latter valid+accomplished step in _statuses array  with currentCheckpointGroup
    this._statuses.forEach((status) => {
      progressSteps.forEach((step) => {
        if (step.status === status.status && step.checkpointGroup === currentCheckpointGroup) {
          lastValidStep = step;
        }
      });
    });

    if (!lastValidStep) {
      throw new Error('No last valid step found in findLastValidStep');
    }

    // Find the first step in the stage that contains that lastValidStep
    Object.values(this._progressStages)
      .filter((stage) => !!stage)
      .forEach((stage) => {
        stage.find((step) => {
          if (step.status === lastValidStep?.status) {
            lastStep = stage[0];
          }
        });
      });

    return lastStep;
  };

  // Finds the non-active step from the last accomplished steps
  private findLastNonActiveStep = (): ProgressStep | undefined => {
    const lastStatuses = this.findLastStatuses();

    if (!lastStatuses?.length) {
      return undefined;
    }

    const nonActiveStatus = lastStatuses.find((status) => this.isNonActiveStatus(status.status));

    if (!nonActiveStatus) {
      return undefined;
    }

    const nonActiveStages = this.getNonActiveStages();
    const lastNonActiveStep = nonActiveStages[nonActiveStatus.status][0];
    return lastNonActiveStep;
  };

  private findLastStatusInCurrentStep(): StatusView | undefined {
    if (!this._statuses || !this._currentStep) {
      return undefined;
    }
    const lastStatus = this.findLastStatus();

    if (!lastStatus) {
      return undefined;
    }

    const currentStageSteps = this._progressStages[this._currentStep.nameTextId] as ProgressStep[];

    if (!currentStageSteps || !currentStageSteps.length) {
      return undefined;
    }

    return currentStageSteps.find((step) => step.status === lastStatus.status) as StatusView | undefined;
  }

  // Find the lattermost status in _statuses array that has the latest updatedAt date
  private findLastStatus(): StatusView | undefined {
    if (!this._statuses) {
      return undefined;
    }

    return this._statuses.reduce((latest, current) => {
      if (!latest) return current;
      return current.updatedAt >= latest.updatedAt ? current : latest;
    }, undefined as StatusView | undefined);
  }

  // Get an array of the statuses with the latest updatedAt date
  private findLastStatuses(): StatusView[] | undefined {
    if (!this._statuses) {
      return undefined;
    }

    const maxUpdatedAt = this._statuses.reduce((latest, current) => {
      if (!latest) return current.updatedAt;
      return current.updatedAt > latest ? current.updatedAt : latest;
    }, undefined as Date | undefined);

    if (!maxUpdatedAt) {
      return undefined;
    }

    return this._statuses.filter((status) => {
      return status.updatedAt.getTime() === maxUpdatedAt.getTime();
    });
  }

  // Checks if any of the latest statuses is a completed status
  private checkIfApplicationIsCompleted(): boolean {
    if (!this._statuses || !this._statuses.length) {
      return false;
    }

    const lastStatuses = this.findLastStatuses()!;

    if (!lastStatuses.length) {
      return false;
    }

    return lastStatuses.some((status) => this.isCompletedStatus(status.status));
  }

  protected checkIfApplicationIsActive(): boolean {
    if (!this._statuses || !this._progressStages || !this._statuses.length) return false;

    const lastStatuses = this.findLastStatuses();

    if (!lastStatuses?.length) {
      return false;
    }

    return !lastStatuses.some((status) => this.isNonActiveStatus(status.status));
  }

  protected getProgressPercentage(): number | undefined {
    const statuses = this._statuses;
    const currentStep = this._currentStep;

    if (!statuses || !currentStep) {
      return;
    }

    const visibleProgressSteps = this.getProgressStepsFromStages().filter((step) => !step.isHidden);

    const progressPercentage = new ProgressPercentage(visibleProgressSteps, currentStep, this._isApplicationCompleted);

    return progressPercentage.percentage;
  }

  public getProgressStepDetails(step: ProgressStep) {
    const currentCheckpointGroup = this.getCurrentCheckpointGroup();
    const isInCheckpointGroupBehind = currentCheckpointGroup ? step.checkpointGroup < currentCheckpointGroup : false;

    const hasPreviousStepsAhead = this.checkForPreviousActiveStepsAheadInStatusesArray(step);

    const lastOccurrence = this.findLastStatusOccurrence(step.status);

    const isChecked = !!lastOccurrence && (isInCheckpointGroupBehind || !hasPreviousStepsAhead);

    if (!isChecked) {
      return {
        name: this.getStatusName(step.status),
        status: this.isPreviousStepChecked(step) ? ProgressStepStatus.PENDING : ProgressStepStatus.FUTURE,
        updatedAt: null,
        detail: null,
      };
    }

    return this.mapProgressStatus(lastOccurrence);
  }

  private getCurrentCheckpointGroup = () => {
    if (!this._statuses) {
      return 1;
    }

    // Analyze the corresponding ProgressStep of each accomplished status (in _statuses array)
    // Return the highest checkpoint group number reached among them
    let currentCheckpointGroup = 1;
    const progressSteps = this.getProgressStepsFromStages();

    this._statuses.forEach((status) => {
      const step = progressSteps.find((step) => step.status === status.status);
      if (step && step.checkpointGroup > currentCheckpointGroup) {
        currentCheckpointGroup = step.checkpointGroup;
      }
    });

    return currentCheckpointGroup;
  };

  // Check if a step's corresponding status has any active status ahead in the _statuses array that fulfills the following conditions:
  // - is active
  // - corresponds to a lower index in progressSteps (despite being ahead in _statuses array)
  // - its checkpointGroup number is the same or greater (it can't be greater as progressSteps is ordered by checkpointGroup?)
  private checkForPreviousActiveStepsAheadInStatusesArray = (step: ProgressStep) => {
    if (!this._statuses) {
      return false;
    }

    // If step is not an active, return false
    if (this.isStatusNonActive(step.status)) {
      return false;
    }

    const progressSteps = this.getProgressStepsFromStages();

    // If step is the first step in progressSteps, return false
    const stepIndex = progressSteps.findIndex((item) => item.status === step.status);
    if (!stepIndex) {
      return false;
    }

    const activeStatuses = this._statuses.filter((statusView) => {
      return !this.isStatusNonActive(statusView.status);
    });
    const indexInActiveStatuses = activeStatuses.findIndex((status) => status.status === step.status);
    const activeStatusesAhead = activeStatuses.slice(indexInActiveStatuses);

    return activeStatusesAhead.some((statusAhead) => {
      const indexOfStatusAhead = progressSteps.findIndex((stage) => stage.status === statusAhead.status);
      const stepAhead = progressSteps[indexOfStatusAhead];

      return indexOfStatusAhead < stepIndex && stepAhead && stepAhead.checkpointGroup >= step.checkpointGroup;
    });
  };

  private findLastStatusOccurrence(stepStatus: Status) {
    if (!this._statuses) {
      return undefined;
    }

    const reversedStatuses = [...this._statuses].reverse();
    const lastIndex = reversedStatuses.findIndex((statusObject) => statusObject.status === stepStatus);

    if (lastIndex === -1) {
      return undefined;
    }

    return reversedStatuses[lastIndex];
  }

  private isPreviousStepChecked(step: ProgressStep): boolean {
    if (!this._statuses) {
      return false;
    }

    // Find previous step
    const progressSteps = this.getProgressStepsFromStages();
    const stepIndex = progressSteps.findIndex((progressStep) => progressStep.status === step.status);
    if (!stepIndex || stepIndex === -1) {
      return false;
    }
    const previousStep = progressSteps[stepIndex - 1];

    // If previous step not completed, return false
    if (!this._statuses.some((status) => status.status === previousStep.status)) {
      return false;
    }

    // If active statuses ahead of previous step status in _statuses array, correspond to previous steps and have been completed, return false
    const hasPreviousStatusPreviousStepsAhead = this.checkForPreviousActiveStepsAheadInStatusesArray(previousStep);
    if (hasPreviousStatusPreviousStepsAhead) {
      return false;
    }

    return true;
  }

  private mapProgressStatus(status: StatusView) {
    const isCancelledStatus = this.isStatusNonActive(status.status);

    return {
      name: isCancelledStatus ? status.status : this.getStatusName(status.status),
      status: isCancelledStatus ? ProgressStepStatus.CANCELLED : ProgressStepStatus.COMPLETED,
      updatedAt: status.updatedAt,
      detail: status.detail,
    };
  }

  private getNonActiveStatuses(): Status[] {
    return Object.keys(this.getNonActiveStages()) as Status[];
  }

  public isStatusNonActive(status: Status): boolean {
    return this.getNonActiveStatuses().includes(status);
  }

  public isCurrentStepNonActive(): boolean {
    if (!this._currentStep) {
      return false;
    }

    return this.isStatusNonActive(this._currentStep.status);
  }

  public getDesistedReason(): string | undefined {
    if (!this._lastStatus) {
      return undefined;
    }

    if (
      [LoanStatus.STATUS_DESISTED, LoanStatusExternal.DESISTED].includes(
        this._lastStatus.status as LoanStatus | LoanStatusExternal,
      )
    ) {
      return this._lastStatus.detail ?? undefined;
    }

    return undefined;
  }

  public get statuses(): StatusView[] | undefined {
    return this._statuses;
  }

  public get progressStages(): Partial<MilestoneProgressStages> {
    return this._progressStages;
  }

  public get currentStep(): ProgressStep | undefined {
    return this._currentStep;
  }

  public get lastStatusInCurrentStep(): StatusView | undefined {
    return this._lastStatusInCurrentStep;
  }

  public get isActive(): boolean {
    return this._isApplicationActive;
  }

  public get isCompleted(): boolean {
    return this._isApplicationCompleted;
  }

  public get progressPercentage(): number | undefined {
    return this._progressPercentage;
  }
}
